import { useEffect, useRef, useState } from 'react'
import { api, type Vendor } from '../lib/api'
import { BandChip } from './BandChip'
import { Avatar, Icon } from './ui'

// The search for a vendor. The sidebar's "Jump to" box (App.tsx) finds a
// screen; this finds a record.
//
// It does two jobs from one box, because two boxes on one screen is the thing
// people notice first and trust least. Typing searches the register by name,
// code or PAN and offers the matches; Enter opens the highlighted one. On a
// screen that lists vendors it also narrows that list as you type, so the
// register, the portfolio and this box never disagree about what "search"
// means.
//
// This box has no keyboard shortcut: it is the first thing on the page. "/"
// goes to "Jump to", which says so in its own hint.

// Screens whose list this box narrows. Anywhere else it is a jump-to.
function narrows(hash: string): boolean {
  const head = hash.replace(/^#\/?/, '').split('?')[0]?.split('/')[0] ?? ''
  return head === 'vendors' || head === 'portfolio'
}

function name(v: Vendor): string {
  return v.displayName?.trim() || v.legalName
}

export function QuickSearch() {
  const [q, setQ] = useState('')
  const [rows, setRows] = useState<Vendor[]>([])
  const [open, setOpen] = useState(false)
  const [sel, setSel] = useState(0)
  const [busy, setBusy] = useState(false)
  const box = useRef<HTMLInputElement>(null)
  const timer = useRef<number>(0)
  const [onList, setOnList] = useState(() => narrows(window.location.hash))

  useEffect(() => {
    const sync = () => setOnList(narrows(window.location.hash))
    window.addEventListener('hashchange', sync)
    return () => window.removeEventListener('hashchange', sync)
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // The list screens listen for this rather than keeping a box of their own.
  // An event rather than the address bar: a filter that rewrote the URL on
  // every keystroke would fill the back button with half-typed words.
  useEffect(() => {
    const t = window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('tprm:query', { detail: q.trim() }))
    }, 250)
    return () => window.clearTimeout(t)
  }, [q])

  useEffect(() => {
    window.clearTimeout(timer.current)
    if (!q.trim()) {
      setRows([])
      return
    }
    timer.current = window.setTimeout(() => {
      setBusy(true)
      api
        .vendors({ q: q.trim() })
        .then((p) => {
          setRows((p.vendors ?? []).slice(0, 8))
          setSel(0)
          setOpen(true)
        })
        .catch(() => setRows([]))
        .finally(() => setBusy(false))
    }, 180)
    return () => window.clearTimeout(timer.current)
  }, [q])

  const go = (v: Vendor | undefined) => {
    if (!v) return
    window.location.hash = '#/vendor/' + v.id
    setOpen(false)
    setQ('')
    box.current?.blur()
  }

  return (
    <div className="qs" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false) }}>
      <Icon name="search" size={16} />
      <input
        ref={box}
        type="search"
        placeholder={onList ? 'Filter this list, or find a vendor' : 'Find a vendor'}
        aria-label="Find a vendor"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => rows.length && setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setSel((s) => Math.min(rows.length - 1, s + 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setSel((s) => Math.max(0, s - 1))
          } else if (e.key === 'Enter') {
            e.preventDefault()
            go(rows[sel])
          }
        }}
      />
      {open && q.trim() && (
        <div className="qs-list" role="listbox">
          {rows.length === 0 && <div className="qs-empty">{busy ? 'Searching' : 'No vendor matches ' + q.trim()}</div>}
          {rows.map((v, i) => (
            <a key={v.id} href={'#/vendor/' + v.id} role="option" aria-selected={i === sel} onMouseDown={(e) => { e.preventDefault(); go(v) }}>
              <Avatar name={name(v)} size={26} />
              <span>{name(v)}</span>
              {v.currentBand && <BandChip band={v.currentBand} />}
              <span className="code">{v.vendorCode}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  )
}
